/* eslint-disable i18next/no-literal-string */
import Header from "~/components/header/Header";
import Footer from "~/components/home/Footer";
import {
  LandingPageHeaderH2,
  LandingPageSubheader,
} from "~/components/landing/LandingText";
import Pricing from "~/components/landing/Pricing";
import AllFeatures, { ShortSentences } from "~/components/landing/AllFeatures";
import DeviceFeatures from "~/components/landing/DeviceFeatures";
import PrivacyPromised from "~/components/landing/PrivacyPromised";
import EasyInstallation, {
  EasyInstallationSubText,
} from "~/components/landing/EasyInstallation";
import ObdCompatibility from "~/components/landing/ObdCompatibility";
import Reviews from "~/components/landing/Reviews";
import MiscGridRentals from "~/components/landing/MiscGridRentals";
import MonitoringHighlightsGrid, {
  type MonitoringHighlightsCopy,
} from "~/components/landing/MonitoringHighlightsGrid";
import HumHero from "~/components/hum-by-verizon/HumHero";
import HumExplanation from "~/components/hum-by-verizon/HumExplanation";
import HumFaq from "~/components/hum-by-verizon/HumFaq";
import { imageUrl } from "~/lib/image-url";

export const handle = { sitemap: { title: "Hum by Verizon" } };

// Plain public/ string path, not a JS import — see the comment on bgImageSet in
// app/components/home/UseCases.tsx for why.
const satisfiedCustomers = imageUrl("/images/landing/three-satisfied-fleet-customers.jpg");

// Ported from bouncieweb's `pages/hum-by-verizon.js` -> `components/LandingHum.js`
// (rendered there with the title/description below and `footerLite` defaulting
// to false, so the full Footer is used).
// Faithful reproduction: same section order, copy, and layout as the original.
//
// Deviations, both user-approved and documented where they live:
//   - HumFaq composes the shared Radix accordion rather than the original's
//     hand-rolled div, which drops the max-height caps that clipped FAQ 3.
//   - A canonical tag is added. The original Hum page has none, but all three
//     sibling ports do, and this is the one landing page bouncieweb wants
//     indexed (`seo/ignored-paths.js` comments it out of the ignore list).
//
// `ObdCompatibility`'s custom heading and `EasyInstallation`'s pre-heading are JSX
// here, so the curly apostrophe renders as a character. The original passed
// plain JS strings containing the literal text `&rsquo;`, which React does not
// HTML-decode — so production shows the raw entity. The sibling ports already
// fixed this the same way.

// The seven strings that make the shared monitoring grid this page's. Copied
// verbatim from /turo, whose wording production also renders here — the original
// mounts the same `HighlightsGridTuro` with no overrides.
const highlightsCopy: MonitoringHighlightsCopy = {
  locationSubtitle: "Real-time visibility.",
  locationBody: (
    <>
      Whether it&rsquo;s one vehicle or a growing fleet, Bouncie captures every
      second of each trip, past and present, all on one live map. From handoff to
      drop-off, you&rsquo;ll always know where your vehicle is. Host with
      confidence!
    </>
  ),
  habitsOverlayTitle: "Their driving style.",
  habitsBody: (
    <>
      Get instant insights on how your guest drives: speed, acceleration, hard
      braking, and more. Safer trips start with visibility. Their habits. Your
      advantage!
    </>
  ),
  accidentBody: (
    <>
      Bouncie&rsquo;s advanced sensors and software detect accidents the moment
      they happen and instantly send you an SMS alert. No guesswork, no delay.
      React quickly. Protect what matters.
    </>
  ),
  weatherBody: (
    <>
      We&rsquo;re watching out for you. When a severe weather advisory is issued
      near your vehicle, Bouncie automatically sends a message with all the
      details. We got this!
    </>
  ),
  geoZonesBody: (
    <>
      Get notified the moment your vehicle enters or exits a zone you set.
      Whether it&rsquo;s a pickup spot, drop-off location, or off-limits area,
      Bouncie keeps you one step ahead. No checking required!
    </>
  ),
};

export default function HumByVerizon() {
  return (
    <>
      {/* React 19 hoists these into the document <head> (no wrapper element —
          a nested <head> collides with root.tsx's document head). */}
      <title>Bouncie Helps Hum by Verizon Users Stay Connected</title>
      <meta
        name="description"
        content="Hum by Verizon is ending, but your connected experience doesn’t have to. Switch to Bouncie for smarter driving and the reliability you trust."
      />
      <link rel="canonical" href="https://www.bouncie.com/hum-by-verizon" />

      <Header />

      {/* LandingPageGlobalStyles: color fadedBlack2 + balanced text wrap */}
      <div className="[text-wrap:balance] text-[#0B1019]">
        <HumHero />
        <HumExplanation />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <LandingPageHeaderH2>
            Stay connected to what moves your life.
          </LandingPageHeaderH2>
          <LandingPageSubheader>
            Keep the connected experience you loved from Hum, with Bouncie.
          </LandingPageSubheader>
          <MiscGridRentals />
        </div>

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <LandingPageHeaderH2>Know more. Worry&nbsp;less.</LandingPageHeaderH2>
          <LandingPageSubheader>
            Protecting what matters most.
          </LandingPageSubheader>
          <MonitoringHighlightsGrid {...highlightsCopy} />
          <Pricing
            className="mb-12"
            showQuoteButton
            quoteButtonText="Build a Quote for Your Fleet"
          />
        </div>

        {/* No AddToCartButton here — unlike /turo and /shoploaner, the original
            passes only the three short sentences as bottomContent. */}
        <AllFeatures
          bottomContent={
            <div className="mt-4 mb-8">
              <ShortSentences>No contracts.</ShortSentences>
              <ShortSentences>No hidden fees.</ShortSentences>
              <ShortSentences>No activation fees.</ShortSentences>
            </div>
          }
        />

        {/* eslint-disable-next-line local/no-wrap-legacy -- legacy port, not yet redesigned */}
        <div className="wrap-legacy">
          <LandingPageHeaderH2>Frequently Asked Questions</LandingPageHeaderH2>
          <HumFaq />
          <DeviceFeatures />
          <PrivacyPromised className="mt-0 mb-16" />
          <EasyInstallation
            comboBottom
            preHeading={<>Yes, it&rsquo;ll work in your vehicle!</>}
            heading={
              <>
                <EasyInstallationSubText>No tools.</EasyInstallationSubText>
                <EasyInstallationSubText>No wires.</EasyInstallationSubText>
                <EasyInstallationSubText>
                  It just plugs in.
                </EasyInstallationSubText>
              </>
            }
          />
          {/* EasyInstallation's "not compatible" button scrolls to this id, and
              FAQ 5 anchors to it — it must stay between the two sections. */}
          <div id="obd-compatibility-scroll-target" />
          <ObdCompatibility
            comboTop
            customMessage={<>Confirm your vehicle&rsquo;s compatibility</>}
          />
          <Reviews
            carAndDriverImageAlt="Two women on a journey in a car"
            forbesImage={satisfiedCustomers}
            forbesImageAlt="Three smiling customers"
          />
        </div>

        <Footer />
      </div>
    </>
  );
}
